import { useMacroStore, type PinnedSeries, type MacroDataPoint } from '@/store/macroStore';
import { trackMacroAction, trackMacroVisibility, trackMacroDetailOpen } from '@/services/gtag';

interface MacroCardProps {
  pinned: PinnedSeries;
}

/** Derive a short ticker-like label from a series_id (e.g. LK_OPR → OPR) */
function shortLabel(seriesId: string): string {
  const parts = seriesId.split('_');
  if (parts.length > 1) return parts.slice(1).join(' ');
  return seriesId;
}

/** Format a value: big-number suffixes (K, Mn, Bn, Tr) and 1-decimal for % */
function fmtValue(value: number | undefined, unit: string | null | undefined): string {
  if (value === undefined || value === null || isNaN(value)) return '—';

  // Percentages: always 1 decimal place
  if (unit === '%') {
    return value.toFixed(1);
  }

  // Big-number formatting for large values
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000_000_000) return sign + (abs / 1_000_000_000_000).toFixed(2) + 'Tr';
  if (abs >= 1_000_000_000)     return sign + (abs / 1_000_000_000).toFixed(2) + 'Bn';
  if (abs >= 1_000_000)         return sign + (abs / 1_000_000).toFixed(2) + 'Mn';
  if (abs >= 1_000)             return sign + (abs / 1_000).toFixed(1) + 'K';

  // Small numbers
  return abs < 1 ? value.toFixed(4) : value.toFixed(2);
}

/** Latest data point */
function latestPoint(data: MacroDataPoint[] | undefined): MacroDataPoint | undefined {
  if (!data || data.length === 0) return undefined;
  return data[data.length - 1];
}

/** ρ display color */
function correlationColor(rho: number | null): string {
  if (rho === null) return 'text-muted';
  const abs = Math.abs(rho);
  if (abs >= 0.5) return 'text-emerald-400';
  if (abs >= 0.3) return 'text-yellow-400';
  return 'text-muted';
}

export function MacroCard({ pinned }: MacroCardProps) {
  const seriesData = useMacroStore((s) => s.seriesData[pinned.seriesId]);
  const seriesMeta = useMacroStore((s) => s.seriesMeta.find((m) => m.series_id === pinned.seriesId));
  const setDetailSeriesId = useMacroStore((s) => s.setDetailSeriesId);
  const removeSeries = useMacroStore((s) => s.removeSeries);
  const toggleVisibility = useMacroStore((s) => s.toggleVisibility);
  const rho = useMacroStore((s) => s.correlations[pinned.seriesId] ?? null);

  const latest = latestPoint(seriesData);

  const label = shortLabel(pinned.seriesId);
  const name = seriesMeta?.name ?? pinned.seriesId;
  const unit = latest?.unit ?? null;

  return (
    <div
      className="relative flex flex-col gap-0.5 px-3 py-1.5 rounded-lg cursor-pointer select-none shrink-0 min-w-[110px] max-w-[160px]
        bg-panel-bg/60 border border-border hover:bg-panel-hover/60 transition-colors group"
      style={{ borderLeftWidth: 3, borderLeftColor: pinned.color }}
      onClick={() => {
        trackMacroDetailOpen(pinned.seriesId);
        setDetailSeriesId(pinned.seriesId);
      }}
      title={name}
    >
      {/* Controls */}
      <div className="absolute top-1 right-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-chart-overlay">
        {/* Visibility toggle */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            trackMacroVisibility(pinned.seriesId, !pinned.visible);
            toggleVisibility(pinned.seriesId);
          }}
          title={pinned.visible ? 'Hide pane' : 'Show pane'}
          className="p-0.5 rounded text-muted hover:text-text hover:bg-panel-hover transition-colors"
        >
          {pinned.visible ? (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          ) : (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
              <line x1="1" y1="1" x2="23" y2="23"/>
            </svg>
          )}
        </button>
        {/* Remove */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            trackMacroAction('remove', pinned.seriesId);
            removeSeries(pinned.seriesId);
          }}
          title="Remove macro indicator"
          className="p-0.5 rounded text-muted hover:text-red-400 hover:bg-red-400/10 transition-colors"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      {/* Series label */}
      <span className="text-[10px] font-semibold text-text truncate pr-8">{label}</span>

      {/* Latest value */}
      <span className="text-[12px] font-medium text-text leading-tight">
        {fmtValue(latest?.value, unit)}
      </span>

      {/* Period + correlation */}
      <div className="flex items-center gap-1.5 mt-0.5">
        {latest?.date && (
          <span className="text-[9px] text-muted">{latest.date.slice(0, 7)}</span>
        )}
        {rho !== null && (
          <span className={`text-[9px] font-medium ${correlationColor(rho)}`}>
            ρ {rho >= 0 ? '+' : ''}{rho.toFixed(2)}
          </span>
        )}
      </div>
    </div>
  );
}
